import { Ban, Clock, Edit, PhoneOff, Unlock } from "lucide-react";
import { Badge } from "../../../../../components/ui/badge";
import { Button } from "../../../../../components/ui/button";
import { Card, CardContent } from "../../../../../components/ui/card";
import {
  SimpleColumn,
  SimpleSortableTable,
} from "../../../../../components/ui/simple-sortable-table";
import { formatCnpj } from "../../../utils/format-cnpj";
import { formatCpf } from "../../../utils/format-cpf";
import { formatDate } from "../../../utils/format-date";
import { CadastroDocument } from "../types";
import { BloqueioStatus } from "./bloqueio-status";

interface ResultsTableProps {
  documents: CadastroDocument[];
  onEdit: (document: CadastroDocument) => void;
  onBloquearDocumento: (document: CadastroDocument) => void;
  onDesativarTelefone: (document: CadastroDocument) => void;
  onDesbloquear: (document: CadastroDocument) => void;
}

export function ResultsTable({
  documents,
  onEdit,
  onBloquearDocumento,
  onDesativarTelefone,
  onDesbloquear,
}: ResultsTableProps) {
  const formatPhone = (phone?: string) => {
    if (!phone) {
      return "";
    }
    const numbersOnly = phone.replace(/\D/g, "");

    if (numbersOnly.startsWith("55") && numbersOnly.length <= 13) {
      return numbersOnly
        .replace(/^(\d{2})(\d{2})(\d)/, "+$1 ($2) $3")
        .replace(/(\d{4})(\d)/, "$1-$2");
    }

    return phone;
  };

  if (documents.length === 0) {
    return null;
  }

  // Cadastro com o maior created_at; só marca quando há mais de um.
  const createdAtTs = (doc: CadastroDocument) =>
    doc.created_at ? new Date(doc.created_at).getTime() : NaN;
  const maisRecente =
    documents.length > 1
      ? documents.reduce<CadastroDocument | null>((acc, doc) => {
          const ts = createdAtTs(doc);
          if (Number.isNaN(ts) || (acc && createdAtTs(acc) >= ts)) return acc;
          return doc;
        }, null)
      : null;

  const formatCreatedAt = (value?: string) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      dateStyle: "short",
      timeStyle: "short",
    });
  };

  const columns: SimpleColumn<CadastroDocument>[] = [
    {
      key: "id",
      label: "ID",
      cellClassName: "font-medium",
    },
    {
      key: "nome_completo",
      label: "Nome Completo",
    },
    {
      key: "created_at",
      label: "Cadastrado em",
      sortValue: (doc) => createdAtTs(doc) || 0,
      cellClassName: "whitespace-nowrap",
      render: (doc) => (
        <div className="flex flex-col items-start gap-1">
          <span>{formatCreatedAt(doc.created_at)}</span>
          {doc === maisRecente && (
            <Badge className="gap-1 bg-blue-100 text-blue-700 hover:bg-blue-100">
              <Clock className="h-3 w-3" />
              Mais recente
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: "data_nascimento",
      label: "Data Nascimento",
      render: (doc) => formatDate(doc.data_nascimento),
    },
    {
      key: "nao_aprovado_azumi",
      label: "Status Azumi",
      sortValue: (doc) =>
        doc.nao_aprovado_azumi === true
          ? 0
          : doc.nao_aprovado_azumi === false
            ? 1
            : 2,
      render: (doc) =>
        doc.nao_aprovado_azumi === true ? (
          <span className="inline-flex items-center text-xs font-semibold text-red-600">
            ❌ Não Cadastrado
          </span>
        ) : doc.nao_aprovado_azumi === false ? (
          <span className="inline-flex items-center text-xs font-semibold text-green-600">
            ✅ Cadastrado
          </span>
        ) : (
          <span className="inline-flex items-center text-xs font-semibold text-orange-600">
            ❓ Indefinido
          </span>
        ),
    },
    {
      key: "cpf",
      label: "CPF",
      render: (doc) => formatCpf(doc.cpf),
    },
    {
      key: "cnpj",
      label: "CNPJ",
      render: (doc) => formatCnpj(doc.cnpj),
    },
    {
      key: "telefone",
      label: "Telefone",
      render: (doc) => formatPhone(doc.telefone),
    },
    {
      key: "ops",
      label: "OPS",
      render: (doc) => doc.ops || "-",
    },
    {
      key: "tipo_emissao_nf",
      label: "Tipo Emissão NF",
      render: (doc) => {
        const tipo = doc.tipo_emissao_nf;
        if (!tipo) return "N/A";
        const labels: Record<string, string> = {
          propria: "Própria",
          en: "ZapCash",
          dues: "Dues",
        };
        return labels[tipo] || tipo;
      },
    },
    {
      key: "chave_pix",
      label: "Chave Pix",
      render: (doc) => doc.chave_pix || "-",
    },
    {
      key: "tipo_chave_pix",
      label: "Tipo Chave Pix",
      render: (doc) => doc.tipo_chave_pix || "-",
    },
    {
      key: "tipo_titularidade_pix",
      label: "Titularidade Pix",
      render: (doc) => doc.tipo_titularidade_pix || "-",
    },
    {
      key: "bloqueio_id",
      label: "Oferta",
      sortValue: (doc) => (doc.bloqueio_id ? 0 : 1),
      render: (doc) => <BloqueioStatus doc={doc} />,
    },
    {
      key: "ifood_uuid",
      label: "iFood UUID",
      cellClassName: "max-w-[150px] truncate",
      render: (doc) => doc.ifood_uuid || "-",
    },
    {
      key: "acoes",
      label: "Ações",
      sortable: false,
      headClassName: "text-right",
      cellClassName: "text-right",
      render: (doc) => (
        <div className="flex justify-end gap-1">
          <Button size="sm" variant="outline" onClick={() => onEdit(doc)}>
            <Edit className="h-4 w-4 mr-1" />
            Editar
          </Button>
          {doc.bloqueio_id ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onDesbloquear(doc)}
            >
              <Unlock className="h-4 w-4 mr-1" />
              Desbloquear
            </Button>
          ) : (
            <>
              <Button
                size="sm"
                variant="outline"
                title="Desativar este telefone: a oferta passa a usar outro cadastro"
                onClick={() => onDesativarTelefone(doc)}
              >
                <PhoneOff className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-red-600"
                title="Bloquear o CPF/CNPJ nesta ops"
                onClick={() => onBloquearDocumento(doc)}
              >
                <Ban className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <Card>
      <CardContent className="pt-6">
        <h2 className="text-xl font-bold text-foreground mb-4">
          Cadastros Encontrados ({documents.length})
        </h2>
        <div className="overflow-x-auto">
          <SimpleSortableTable
            columns={columns}
            rows={documents}
            rowKey={(doc) => doc.id}
            rowClassName={(doc) =>
              doc === maisRecente ? "bg-blue-50/60" : undefined
            }
          />
        </div>
      </CardContent>
    </Card>
  );
}
